import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import type {
  FeedbackType,
  SubmitFeedback,
  SubmitFeedbackParams,
  UploadFeedbackAttachment,
} from '@/modules/feedback/use-cases'
import type { FeedbackEnvironment } from '@/modules/feedback/use-cases'
import { reportError } from '@/shared/infra/observability'
import { useOrgContext, useSubmitGuard } from '@/shared/ui/hooks'
import { useToast } from '@/shared/ui/providers'
import {
  deriveFeatureFromPathname,
  deriveFeatureOptions,
  personaForOrgType,
  type FeatureOption,
} from './derive-feature-options'

type UseFeedbackDialogProps = {
  onSubmitFeedback: SubmitFeedback
  onUploadFeedbackAttachment: UploadFeedbackAttachment
  isOpen: boolean
  onClose: () => void
  onSubmitted: () => void
}

export type UseFeedbackDialogResult = {
  feedbackType: FeedbackType
  selectedFeature: string
  message: string
  isSubmitting: boolean
  errorMessage: string | null
  featureOptions: FeatureOption[]
  environmentDetails: FeedbackEnvironment
  selectedFile: File | null
  attachmentPreviewUrl: string | null
  handleTypeChange: (type: FeedbackType) => void
  handleFeatureChange: (feature: string) => void
  handleMessageChange: (msg: string) => void
  handleFileSelect: (file: File | null) => void
  handleClearAttachment: () => void
  handleSubmit: () => Promise<void>
}

function detectBrowser(ua: string): string {
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome'
  if (ua.includes('Edg')) return 'Edge'
  if (ua.includes('Firefox')) return 'Firefox'
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari'
  return 'Unknown'
}

export function useFeedbackDialog({
  onSubmitFeedback,
  onUploadFeedbackAttachment,
  isOpen,
  onClose,
  onSubmitted,
}: UseFeedbackDialogProps): UseFeedbackDialogResult {
  const { orgId, orgType } = useOrgContext()
  const toast = useToast()
  const { pathname } = useLocation()
  const { isSubmitting, runGuarded } = useSubmitGuard()

  const featureOptions = useMemo(
    () => deriveFeatureOptions(personaForOrgType(orgType)),
    [orgType],
  )

  const [feedbackType, setFeedbackType] = useState<FeedbackType>('idea')
  const [message, setMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [attachmentPreviewUrl, setAttachmentPreviewUrl] = useState<string | null>(null)
  const [selectedFeature, setSelectedFeature] = useState(() =>
    deriveFeatureFromPathname(pathname, featureOptions),
  )

  const environmentDetails = useMemo<FeedbackEnvironment>(
    () => ({
      browser: detectBrowser(navigator.userAgent),
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      viewportSize: `${window.innerWidth}x${window.innerHeight}`,
      userAgent: navigator.userAgent,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    [],
  )

  useEffect(() => {
    if (isOpen) {
      setFeedbackType('idea')
      setMessage('')
      setErrorMessage(null)
      setSelectedFile(null)
      setSelectedFeature(deriveFeatureFromPathname(pathname, featureOptions))
    }
  }, [isOpen, pathname, featureOptions])

  useEffect(() => {
    if (!selectedFile) {
      setAttachmentPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(selectedFile)
    setAttachmentPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [selectedFile])

  const handleTypeChange = useCallback((type: FeedbackType) => {
    setFeedbackType(type)
  }, [])

  const handleFeatureChange = useCallback((feature: string) => {
    setSelectedFeature(feature)
  }, [])

  const handleMessageChange = useCallback((msg: string) => {
    setMessage(msg)
  }, [])

  const handleFileSelect = useCallback((file: File | null) => {
    setSelectedFile(file)
  }, [])

  const handleClearAttachment = useCallback(() => {
    setSelectedFile(null)
  }, [])

  const handleSubmit = useCallback(async () => {
    if (message.trim().length < 10) {
      toast.error('Message too short', 'Please enter at least 10 characters.')
      return
    }

    if (message.trim().length > 1000) {
      toast.error('Message too long', 'Please keep feedback under 1000 characters.')
      return
    }

    setErrorMessage(null)

    const params: SubmitFeedbackParams = {
      ...(orgId && orgType ? { orgId, orgType } : {}),
      feedbackType,
      feature: selectedFeature,
      message: message.trim(),
      pageUrl: window.location.href,
      appVersion: import.meta.env.VITE_APP_VERSION || 'dev',
      environment: environmentDetails,
      source: 'web',
    }

    await runGuarded(async () => {
      try {
        if (selectedFile) {
          const { path } = await onUploadFeedbackAttachment({ file: selectedFile })
          params.attachmentPath = path
        }
        await onSubmitFeedback(params)
        onSubmitted()
        toast.success('Feedback sent', 'Thank you! We appreciate your input.')
        onClose()
      } catch (err) {
        const msg = reportError(err, { fallbackMessage: 'Failed to submit feedback. Please try again.' })
        setErrorMessage(msg)
        toast.error('Submit failed', msg)
      }
    })
  }, [message, orgId, orgType, feedbackType, selectedFeature, environmentDetails, selectedFile, onUploadFeedbackAttachment, onSubmitFeedback, onSubmitted, onClose, toast, runGuarded])

  return {
    feedbackType,
    selectedFeature,
    message,
    isSubmitting,
    errorMessage,
    featureOptions,
    environmentDetails,
    selectedFile,
    attachmentPreviewUrl,
    handleTypeChange,
    handleFeatureChange,
    handleMessageChange,
    handleFileSelect,
    handleClearAttachment,
    handleSubmit,
  }
}
